import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { RADAR } from "@/content/site";

const today = () => new Date().toISOString().slice(0, 10);
const TEXTS = ["what", "evidence", "why", "question", "relation"];
const empty = () => ({ title: "", source: "", source_url: "", published_on: today(), category: "economia", what: "", evidence: "", why: "", question: "", relation: "", published: true });
const inputCls = "[color-scheme:dark] bg-transparent border-b border-[#3a5a4a] focus:border-[#EAEAE6] outline-none px-1 py-2 text-[#EAEAE6] font-sans text-[14px]";

const Field = ({ label, children }) => (
    <label className="flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans">{label}</span>
        {children}
    </label>
);

const AdminRadar = () => {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(empty());
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState(null);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const load = async () => setItems((await api.get("/radar/admin/all")).data);
    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            if (editingId) await api.put(`/radar/${editingId}`, form);
            else await api.post("/radar", form);
            setForm(empty());
            setEditingId(null);
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Erro ao salvar.");
        }
    };

    const edit = (it) => {
        setEditingId(it.id);
        setForm({ title: it.title, source: it.source, source_url: it.source_url, published_on: it.published_on, category: it.category, published: it.published, ...Object.fromEntries(TEXTS.map((k) => [k, it[k] || ""])) });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        if (!window.confirm("Excluir este item do Radar?")) return;
        await api.delete(`/radar/${id}`);
        await load();
    };

    return (
        <main data-testid="admin-radar" className="max-w-3xl mx-auto px-6 py-14">
            <form onSubmit={submit} className="flex flex-col gap-6 mb-20 border-b border-[#1B3025] pb-14">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#7E8978] font-sans">{editingId ? "Editando item do Radar" : "Novo item do Radar"}</p>
                <Field label="título"><input required data-testid="radar-form-title" value={form.title} onChange={set("title")} className={`${inputCls} font-serif text-[20px]`} /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="fonte (veículo, instituição)"><input required data-testid="radar-form-source" value={form.source} onChange={set("source")} className={inputCls} /></Field>
                    <Field label="link original (obrigatório)"><input type="url" required data-testid="radar-form-url" value={form.source_url} onChange={set("source_url")} className={inputCls} /></Field>
                    <Field label="data"><input type="date" required data-testid="radar-form-date" value={form.published_on} onChange={set("published_on")} className={inputCls} /></Field>
                    <Field label="tema">
                        <select data-testid="radar-form-category" value={form.category} onChange={set("category")} className={`${inputCls} bg-[#0E2018]`}>
                            {Object.entries(RADAR.categorias).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </Field>
                </div>
                {TEXTS.map((k) => (
                    <Field key={k} label={`${RADAR.secoes[k]}${k === "relation" ? " (opcional)" : ""}`}>
                        <textarea rows={k === "question" ? 2 : 3} data-testid={`radar-form-${k}`} value={form[k]} onChange={set(k)} className="bg-transparent border border-[#3a5a4a] focus:border-[#EAEAE6] outline-none p-4 text-[#EAEAE6] font-sans text-[14px] leading-relaxed" />
                    </Field>
                ))}
                <label className="flex items-center gap-3 text-[12px] font-sans text-[#B0B6A6]">
                    <input type="checkbox" checked={form.published} onChange={set("published")} /> publicar (visível no site)
                </label>
                {error && <p data-testid="radar-form-error" className="text-[12px] font-sans text-[#c88a52]">{error}</p>}
                <div className="flex items-center gap-6">
                    <button type="submit" data-testid="radar-form-submit" className="font-sans font-light text-[13px] tracking-[0.06em] uppercase text-[#EAEAE6] border-b border-[#234135] hover:border-[#EAEAE6] pb-2 transition-colors">
                        {editingId ? "Salvar alterações →" : "Publicar no Radar →"}
                    </button>
                    {editingId && (
                        <button type="button" data-testid="radar-form-cancel" onClick={() => { setEditingId(null); setForm(empty()); }} className="font-sans font-light text-[12px] text-[#7E8978] hover:text-[#EAEAE6]">cancelar</button>
                    )}
                </div>
            </form>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#7E8978] font-sans font-light mb-8">Itens do Radar ({items.length})</p>
            <ul className="flex flex-col">
                {items.map((it) => (
                    <li key={it.id} data-testid={`admin-radar-item-${it.id}`} className="border-b border-[#1B3025] py-6 flex items-start justify-between gap-6">
                        <div className="flex-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans mb-2">{it.published_on} · {RADAR.categorias[it.category]} · {it.published ? "publicado" : "rascunho"}</p>
                            <h3 className="font-serif text-[19px] text-[#EAEAE6]">{it.title}</h3>
                            <p className="mt-1 font-sans font-light text-[12px] text-[#B0B6A6]">{it.source}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <button type="button" data-testid={`admin-radar-edit-${it.id}`} onClick={() => edit(it)} className="font-sans font-light text-[11px] uppercase tracking-[0.1em] text-[#B0B6A6] hover:text-[#EAEAE6]">editar</button>
                            <button type="button" data-testid={`admin-radar-delete-${it.id}`} onClick={() => remove(it.id)} className="font-sans font-light text-[11px] uppercase tracking-[0.1em] text-[#7E8978] hover:text-[#EAEAE6]">excluir</button>
                        </div>
                    </li>
                ))}
            </ul>
        </main>
    );
};

export default AdminRadar;
